import React from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { MdAdd, MdClose } from "react-icons/md";
import { Switch } from "@mui/material";
import DateInput from "../ui/DateInput";
import Input from "../ui/Input";

const RentedHouseForm = ({
  control,
  watch,
  setValue,
  isPOI = false,
}) => {
  const {
    fields: houseRentFields,
    append: appendHouseRent,
    remove: removeHouseRent,
  } = useFieldArray({
    control,
    name: "house_rent",
  });

  const isStayingRented = watch("house_rent_applicable") === 1;

  const locationOptions = [
    { label: "Metro", value: "metro" },
    { label: "Non Metro", value: "non-metro" },
  ];

  const financialYearId = watch("financial_year_id");

  const addHouseRent = () => {
    appendHouseRent({
      financial_year_id: financialYearId,
      from_date: "",
      to_date: "",
      address: "",
      landlord_name: "",
      metro: "metro",
      landlord_pan: "",
      rent_amount: "0",
      actual_amount: isPOI ? "0" : undefined,
      total_amount: "0",
      total_actual_amount: isPOI ? "0" : undefined,
    });
  };

  const calculateMonthsDifference = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    const from = fromDate instanceof Date ? fromDate : new Date(fromDate);
    const to = toDate instanceof Date ? toDate : new Date(toDate);
    const yearDiff = to.getFullYear() - from.getFullYear();
    const monthDiff = to.getMonth() - from.getMonth();
    return yearDiff * 12 + monthDiff + 1;
  };

  const calculateTotalAmount = (index) => {
    const watchedFields = watch("house_rent");
    if (watchedFields && watchedFields[index]) {
      const fromDate = watchedFields[index]?.from_date;
      const toDate = watchedFields[index]?.to_date;
      const monthlyAmount = parseFloat(watchedFields[index].rent_amount) || 0;
      const actualMonthlyAmount = parseFloat(watchedFields[index].actual_amount) || 0;

      const months = calculateMonthsDifference(fromDate, toDate);
      const totalAmount = months * monthlyAmount;
      setValue(`house_rent.${index}.total_amount`, totalAmount.toFixed(2));
      if (isPOI) {
        const totalActualAmount = months * actualMonthlyAmount;
        setValue(`house_rent.${index}.total_actual_amount`, totalActualAmount.toFixed(2));
      }
    }
  };

  const handleDateChange = (index) => (date) => {
    setTimeout(() => calculateTotalAmount(index), 0);
  };

  const handleAmountChange = (index) => (e) => {
    setTimeout(() => calculateTotalAmount(index), 0);
  };

  // For POI: handle actual amount change
  const handleActualAmountChange = (index) => (e) => {
    setTimeout(() => calculateTotalAmount(index), 0);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toggle Header */}
      <div className="flex items-center justify-between p-4 bg-white">
        <span className="text-base font-medium text-gray-900">
          Is the employee staying in a rented house?
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {isStayingRented ? "Yes" : "No"}
          </span>
          <Controller
            control={control}
            name="house_rent_applicable"
            render={({ field: { value, onChange } }) => (
              <Switch
                checked={value === 1 ? true : false}
                onChange={(e) => {
                  onChange(e.target.checked ? 1 : 0);
                }}
                color="primary"
                size="medium"
              />
            )}
          />
        </div>
      </div>

      {/* House Rent Details Form */}
      {isStayingRented && (
        <div className="border-t border-gray-200 px-6 py-6 space-y-8 bg-white">
          {houseRentFields.map((field, index) => (
            <div key={field.id} className="space-y-6 relative">
              {/* House Rent Details header with close button */}
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  House Rent Details
                </h2>
                {houseRentFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeHouseRent(index)}
                    className="text-gray-400 hover:text-red-500"
                    aria-label="Remove rented house"
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Form layout - adjust based on isPOI */}
              {isPOI ? (
                // POI Mode: Align with sticky header columns
                <div className="flex w-full gap-4">
                  {/* Particulars Column (1/3) */}
                  <div className="w-1/3 flex flex-col gap-4">
                    {/* Rental Period */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rental Period
                      </label>
                      <div className="flex items-center gap-4">
                        <DateInput
                          label="From"
                          control={control}
                          name={`house_rent.${index}.from_date`}
                          className="w-full"
                          rules={{}}
                          onChangeFn={handleDateChange(index)}
                          props={{
                            dateFormat: "MMM yyyy",
                            showMonthYearPicker: true,
                            placeholderText: "From",
                          }}
                        />
                        <DateInput
                          label="To"
                          control={control}
                          name={`house_rent.${index}.to_date`}
                          className="w-full"
                          rules={{}}
                          onChangeFn={handleDateChange(index)}
                          props={{
                            dateFormat: "MMM yyyy",
                            showMonthYearPicker: true,
                            placeholderText: "To",
                          }}
                        />
                      </div>
                    </div>
                    {/* Address */}
                    <Input
                      control={control}
                      name={`house_rent.${index}.address`}
                      label="Address"
                      isTextarea={true}
                      placeholder="Enter address"
                    />
                    {/* Landlord Name */}
                    <Input
                      control={control}
                      name={`house_rent.${index}.landlord_name`}
                      label="Landlord Name"
                      placeholder="Enter landlord name"
                    />
                    {/* Metro/Non Metro + Landlord PAN */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col space-y-2">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Metro / Non Metro
                        </label>
                        <Controller
                          control={control}
                          name={`house_rent.${index}.metro`}
                          render={({ field: { onChange, value } }) => (
                            <select
                              value={value}
                              onChange={onChange}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-9"
                            >
                              {locationOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                      <Input
                        control={control}
                        name={`house_rent.${index}.landlord_pan`}
                        label="Landlord PAN"
                        placeholder="ABCDE1234F"
                      />
                    </div>
                  </div>

                  {/* Declared Amount Column (1/3) */}
                  <div className="w-1/3 flex flex-col items-center justify-center">
                    <div className="w-full max-w-xs">
                      <Input
                        control={control}
                        name={`house_rent.${index}.rent_amount`}
                        label="Declared Amount /mo."
                        type="number"
                        disabled={true}
                        isCurrency={true}
                        placeholder="0"
                        isPositiveNumber={true}
                      />
                      <div className="mt-2">
                        <Controller
                          control={control}
                          name={`house_rent.${index}.total_amount`}
                          render={({ field: { value } }) => (
                            <span className="text-sm text-gray-600">
                              Total Declared:{" "}
                              {value && parseFloat(value) > 0
                                ? `₹${parseFloat(value).toLocaleString()}`
                                : "0"}
                            </span>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actual Amount Column (1/3) */}
                  <div className="w-1/3 flex flex-col items-center justify-center">
                    <div className="w-full max-w-xs">
                      <Input
                        control={control}
                        name={`house_rent.${index}.actual_amount`}
                        label="Actual Amount /mo."
                        type="number"
                        isCurrency={true}
                        placeholder="0"
                        isPositiveNumber={true}
                        onChangeFn={handleActualAmountChange(index)}
                      />
                      <div className="mt-2">
                        <Controller
                          control={control}
                          name={`house_rent.${index}.total_actual_amount`}
                          render={({ field: { value } }) => (
                            <span className="text-sm text-gray-600">
                              Total Actual:{" "}
                              {value && parseFloat(value) > 0
                                ? `₹${parseFloat(value).toLocaleString()}`
                                : "0"}
                            </span>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Declaration Mode: Original 2-column layout
                <div className="grid grid-cols-2 gap-8">
                  {/* Left column - Address and landlord details */}
                  <div className="flex flex-col gap-4">
                    {/* Rental Period */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rental Period
                      </label>
                      <div className="flex items-center gap-4">
                        <DateInput
                          label="From"
                          control={control}
                          name={`house_rent.${index}.from_date`}
                          className="w-full"
                          rules={{}}
                          onChangeFn={handleDateChange(index)}
                          props={{
                            dateFormat: "MMM yyyy",
                            showMonthYearPicker: true,
                            placeholderText: "From",
                          }}
                        />
                        <DateInput
                          label="To"
                          control={control}
                          name={`house_rent.${index}.to_date`}
                          className="w-full"
                          rules={{}}
                          onChangeFn={handleDateChange(index)}
                          props={{
                            dateFormat: "MMM yyyy",
                            showMonthYearPicker: true,
                            placeholderText: "To",
                          }}
                        />
                      </div>
                    </div>
                    {/* Address */}
                    <Input
                      control={control}
                      name={`house_rent.${index}.address`}
                      label="Address"
                      isTextarea={true}
                      placeholder="Enter address"
                    />
                    {/* Landlord Name */}
                    <Input
                      control={control}
                      name={`house_rent.${index}.landlord_name`}
                      label="Landlord Name"
                      placeholder="Enter landlord name"
                    />
                    {/* Metro/Non Metro + Landlord PAN */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col space-y-2">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Metro / Non Metro
                        </label>
                        <Controller
                          control={control}
                          name={`house_rent.${index}.metro`}
                          render={({ field: { onChange, value } }) => (
                            <select
                              value={value}
                              onChange={onChange}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-9"
                            >
                              {locationOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                      <Input
                        control={control}
                        name={`house_rent.${index}.landlord_pan`}
                        label="Landlord PAN"
                        placeholder="ABCDE1234F"
                      />
                    </div>
                  </div>

                  {/* Right column - Amount */}
                  <div className="flex flex-col mt-7">
                    <Input
                      control={control}
                      name={`house_rent.${index}.rent_amount`}
                      label="Amount /mo."
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      onChangeFn={handleAmountChange(index)}
                    />

                    {/* Total Rent Text Display */}
                    <div className="mt-2">
                      <Controller
                        control={control}
                        name={`house_rent.${index}.total_amount`}
                        render={({ field: { value } }) => (
                          <span className="text-sm text-gray-600">
                            Total Rent :{" "}
                            {value && parseFloat(value) > 0
                              ? `₹${parseFloat(value).toLocaleString()}`
                              : "0"}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addHouseRent}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            <MdAdd className="w-4 h-4" />
            Add Rented House
          </button>
        </div>
      )}
    </div>
  );
};

export default RentedHouseForm;
